import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignmentListQuery,
  AssignmentResponseInput,
  CreateAssignmentInput,
} from '@teentack/shared';
import { applicationKeys } from '@/features/applications/use-applications';
import {
  approveAssignment,
  completeAssignment,
  createAssignments,
  fetchMyAssignments,
  fetchPosterAssignments,
  releasePayment,
  respondToAssignment,
} from './assignments.api';

export const assignmentKeys = {
  all: ['assignments'] as const,
  mine: (query: Partial<AssignmentListQuery>) => ['assignments', 'mine', query] as const,
  poster: (query: Partial<AssignmentListQuery>) => ['assignments', 'poster', query] as const,
};

export function useMyAssignments(query: Partial<AssignmentListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: assignmentKeys.mine(query),
    queryFn: () => fetchMyAssignments(query),
    enabled,
  });
}

export function usePosterAssignments(query: Partial<AssignmentListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: assignmentKeys.poster(query),
    queryFn: () => fetchPosterAssignments(query),
    enabled,
  });
}

/**
 * A fresh key per attempt, reused across retries of that attempt — which is
 * what makes an impatient second click harmless.
 */
export function useCreateAssignments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssignmentInput) => createAssignments(input, crypto.randomUUID()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assignmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: applicationKeys.all });
    },
  });
}

export function useRespondToAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignmentResponseInput & { assignmentId: string }) =>
      respondToAssignment(input.assignmentId, { response: input.response }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assignmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: applicationKeys.all });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* The completion loop [Teen 16/18, Job Poster 26-28/31]                      */
/* -------------------------------------------------------------------------- */

/**
 * These three all move an assignment forward, and every one of them changes
 * what several other screens should say — the poster console, the money
 * ledger, the review prompt. Invalidating broadly is cheaper than reasoning
 * about which of them happens to be mounted.
 */
function useLifecycleMutation<TInput>(
  mutationFn: (input: TInput) => Promise<unknown>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assignmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: applicationKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
      void queryClient.invalidateQueries({ queryKey: ['reviews'] });
      void queryClient.invalidateQueries({ queryKey: ['poster'] });
      void queryClient.invalidateQueries({ queryKey: ['teen'] });
      void queryClient.invalidateQueries({ queryKey: ['parent'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useCompleteAssignment() {
  return useLifecycleMutation((assignmentId: string) => completeAssignment(assignmentId));
}

export function useApproveAssignment() {
  return useLifecycleMutation((assignmentId: string) => approveAssignment(assignmentId));
}

export function useReleasePayment() {
  return useLifecycleMutation((assignmentId: string) =>
    releasePayment(assignmentId, crypto.randomUUID()),
  );
}
